import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload as UploadIcon, CheckCircle } from "@phosphor-icons/react";

export default function ImportPage() {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!file) return toast.error("Select a file");
    setLoading(true); setResult(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/import/xlsx", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      toast.success(`Imported ${r.data.imported} products`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="import-page" className="space-y-6 max-w-3xl min-w-0">
      <div>
        <div className="eyebrow mb-1">Import Center</div>
        <h1 className="font-display font-bold text-3xl lg:text-4xl tracking-tighter text-navy">Bring your catalog in</h1>
        <p className="text-muted-foreground mt-2 text-sm">CSV or XLSX. Expected columns: name, brand, sku, category, mrp, price, stock, description, image_url.</p>
      </div>

      <div className="card-base p-10 text-center border-2 border-dashed">
        <div className="w-14 h-14 rounded-full bg-orange/10 flex items-center justify-center mx-auto mb-4">
          <UploadIcon size={26} weight="duotone" className="text-orange"/>
        </div>
        <div className="font-display font-semibold text-xl text-navy mb-1">Drop your CSV or XLSX</div>
        <div className="text-sm text-muted-foreground mb-6">Duplicates detected automatically by SKU.</div>
        <input data-testid="import-file" type="file" accept=".csv,.xlsx,.xls" onChange={(e)=>setFile(e.target.files?.[0])} className="text-sm mb-4"/>
        <div>
          <button data-testid="import-submit" disabled={loading || !file} onClick={submit} className="btn-primary">
            <UploadIcon size={14} weight="bold"/> {loading ? "Importing…" : "Start Import"}
          </button>
        </div>
      </div>

      {result && (
        <div className="card-base p-6" data-testid="import-result">
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle size={18} weight="fill" className="text-brand-green"/>
            <div className="section-title">Import complete</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat label="Imported" v={result.imported} tone="text-brand-green"/>
            <Stat label="Duplicates" v={result.duplicates} tone="text-orange"/>
            <Stat label="Errors" v={result.errors} tone="text-red-600"/>
            <Stat label="Total rows" v={result.total}/>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, v, tone }) {
  return (
    <div className="min-w-0">
      <div className="eyebrow">{label}</div>
      <div className={`font-display text-3xl font-bold tabular-nums ${tone||"text-navy"}`}>{v}</div>
    </div>
  );
}
